import { useEffect, useRef } from "react";
import { useGoogleApi } from "components/Map/hooks";

/**
 * Reusable floating toolbar rendered on top of a Google Map via the controls API.
 *
 * @param {object}   props
 * @param {object}   props.map   - Google Maps instance
 * @param {string}   props.mapId - Unique map identifier (prevents duplicate controls)
 * @param {Array}    props.items - Array of { id, icon, label, active?, onClick, separator? }
 *                                 If separator is true, a visual divider is inserted before this item.
 */
export default function MapToolbar({ map, mapId, items }) {
    const google = useGoogleApi();
    const containerRef = useRef(null);
    const buttonRefs = useRef({});

    // Create the toolbar container once and push it to the map controls
    useEffect(() => {
        if (!google || !map || !mapId) return;

        const position = google.maps.ControlPosition.TOP_CENTER;
        const controls = map.controls[position];
        const controlId = `map-toolbar-${mapId}`;

        // Don't add if already exists
        if (controls.getArray().some((c) => c.id === controlId)) return;

        const container = document.createElement("div");
        container.id = controlId;
        container.className = "mapToolbar";
        containerRef.current = container;

        controls.push(container);

        return () => {
            const idx = controls.getArray().indexOf(container);
            if (idx !== -1) controls.removeAt(idx);
            containerRef.current = null;
            buttonRefs.current = {};
        };
    }, [google, map, mapId]);

    // Sync buttons into the container whenever items change
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Clear existing children
        container.innerHTML = "";
        buttonRefs.current = {};

        items.forEach((item) => {
            if (item.separator) {
                const sep = document.createElement("div");
                sep.className = "mapToolbar__separator";
                container.appendChild(sep);
            }

            const btn = document.createElement("div");
            btn.id = item.id;
            btn.className = `mapToolbar__button${item.active ? " is-active" : ""}`;
            btn.title = item.label;
            btn.innerHTML = `<i class="icon ${item.icon}"></i><span>${item.label}</span>`;
            const handleClick = (e) => {
                e.stopPropagation();
                item.onClick(e);
            };
            const stopEvent = (e) => e.stopPropagation();
            btn.addEventListener("click", handleClick);
            btn.addEventListener("mousedown", stopEvent);
            btn.addEventListener("mouseup", stopEvent);
            btn.addEventListener("dblclick", stopEvent);
            container.appendChild(btn);
            buttonRefs.current[item.id] = { element: btn, handleClick, stopEvent };
        });

        return () => {
            Object.values(buttonRefs.current).forEach(({ element, handleClick, stopEvent }) => {
                element.removeEventListener("click", handleClick);
                element.removeEventListener("mousedown", stopEvent);
                element.removeEventListener("mouseup", stopEvent);
                element.removeEventListener("dblclick", stopEvent);
            });
        };
    }, [items]);

    return null;
}
